import { useCallback, useEffect, useRef, useState } from "react";
import { apiFetch } from "../lib/api.js";
import { useViewportFrame } from "../hooks/useViewportFrame.js";
import {
  MCP_USAGE_GROUP_OPTIONS,
  dashIfNull,
  formatLastCall,
  isSubjectGroup,
  learnedLabel,
  mcpUsageColumns,
  recordedSinceCaption,
  subjectsRecordedSinceCaption,
  verdictLabel,
  type McpUsageColumn,
  type McpUsageGroupBy,
  type McpUsagePayload,
  type McpUsageRow,
} from "../lib/mcpUsage.js";
import { rangeLastDays } from "./AiUsageDashboard.js";
import { EmptyState, ErrorState, LoadingState } from "./States.js";
import { useConfirm } from "./ConfirmDialog.js";
import { useToast } from "./Toast.js";

/** The App header's tooltip — the consent screen's own qualifier for a self-reported name. */
const APP_QUALIFIER = "as reported by the connecting app";

function cellText(column: McpUsageColumn, row: McpUsageRow, timeZone: string): string {
  const value = row[column.key];
  if (column.key === "last_call_at" || column.key === "opened_at") return formatLastCall(value as string | null | undefined, timeZone);
  if (column.key === "verdict") return verdictLabel(row.verdict, row.broad, row.novel);
  if (column.key === "learned") return learnedLabel(row.closed, row.learned);
  if (typeof value === "number") return value.toLocaleString();
  return dashIfNull(value as string | null | undefined);
}

/**
 * ADR-0206 §4.2 — who calls which Atlas tool on the MCP door, and how often.
 * Laid out like the Internal usage dashboard so the two tabs read as siblings;
 * no token, cost or model figure appears here.
 */
export function McpUsagePanel() {
  const { confirmWithReason } = useConfirm();
  const toast = useToast();
  const [from, setFrom] = useState(() => rangeLastDays(30).from);
  const [to, setTo] = useState(() => rangeLastDays(30).to);
  const [groupBy, setGroupBy] = useState<McpUsageGroupBy>("person");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<McpUsagePayload | null>(null);

  const gridRef = useRef<HTMLDivElement>(null);
  const hasRows = (data?.breakdown.length ?? 0) > 0;
  useViewportFrame(gridRef, { active: !loading && !error && hasRows });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const q = new URLSearchParams({ from, to, group_by: groupBy });
      setData(await apiFetch<McpUsagePayload>(`/api/tenant/ai/usage/mcp?${q.toString()}`));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [from, to, groupBy]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Redact one subject's text. The reason is part of the decision, so it is
   * asked inside the confirmation (the purge precedent), and it names the class
   * of problem, never the content.
   */
  const redactSubject = async (row: McpUsageRow) => {
    if (row.subject_number == null) return;
    const reason = await confirmWithReason({
      title: `Redact subject ${row.subject_number}?`,
      detail:
        "Its text, its goal and the names of what was served for it are replaced with [redacted] on every record of it, " +
        "permanently. Its verdict, counts and dates stay, so the figures here stay honest.",
      confirmLabel: "Redact",
      destructive: true,
      reasonField: { label: "Reason", placeholder: "e.g. a person named in it — never the content", maxLength: 200 },
    });
    if (reason === null) return;
    try {
      await apiFetch(`/api/tenant/ai/usage/mcp/subjects/${row.subject_number}/redact`, { method: "POST", body: { reason } });
      toast.push({ kind: "success", message: `Subject ${row.subject_number} redacted.` });
      await load();
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  };

  const subjects = isSubjectGroup(groupBy);
  const columns = mcpUsageColumns(groupBy);
  const caption = data
    ? subjects
      ? subjectsRecordedSinceCaption(data.subject_summary?.recorded_since ?? null, data.time_zone)
      : recordedSinceCaption(data.summary.recorded_since, data.time_zone)
    : null;
  const s = data?.subject_summary;
  const cards: ReadonlyArray<readonly [string, number]> =
    subjects && s
      ? [
          ["Subjects", s.opens],
          ["Known", s.known],
          ["Partly known", s.partly_known],
          ["Broad", s.broad],
          ["New", s.novel],
          ["Moves", s.moves],
          ["Holds", s.holds],
        ]
      : data
        ? [
            ["Calls", data.summary.calls],
            ["Errors", data.summary.error_calls],
            ["People", data.summary.people],
            ["Tools", data.summary.tools],
          ]
        : [];

  return (
    <div style={{ display: "grid", gap: 12 }}>
      <div className="toolbar">
        <div className="toolbarGroup">
          <label className="field" style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
            <span className="label">From</span>
            <input className="input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label className="field" style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
            <span className="label">To</span>
            <input className="input" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </label>
          <label className="field" style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
            <span className="label">Group by</span>
            <select className="select" value={groupBy} onChange={(e) => setGroupBy(e.target.value as McpUsageGroupBy)}>
              {MCP_USAGE_GROUP_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {error ? <ErrorState detail={error} /> : null}
      {!error && loading ? <LoadingState /> : null}

      {!error && !loading && data ? (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12 }}>
            {cards.map(([label, value]) => (
              <div key={label} className="card" style={{ padding: 12 }}>
                <div className="muted" style={{ fontSize: 12 }}>
                  {label}
                </div>
                <div style={{ fontSize: 22, fontWeight: 700 }}>{value.toLocaleString()}</div>
              </div>
            ))}
          </div>

          {hasRows ? (
            <div className="card adminList">
              <div className="tableWrap tableWrapFrame" ref={gridRef}>
                <table className="table">
                  <thead>
                    <tr>
                      {columns.map((c) => (
                        <th key={c.key} title={c.key === "app" ? APP_QUALIFIER : undefined}>
                          {c.label}
                        </th>
                      ))}
                      {groupBy === "subjects" ? <th /> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {data.breakdown.map((row, i) => (
                      <tr key={i}>
                        {columns.map((c) => (
                          <td key={c.key}>{cellText(c, row, data.time_zone)}</td>
                        ))}
                        {groupBy === "subjects" ? (
                          <td>
                            {row.redacted ? (
                              <span className="pill pillNeutral">Redacted</span>
                            ) : (
                              <div className="adminListActions">
                                <button type="button" className="adminListAction isDanger" onClick={() => void redactSubject(row)}>
                                  Redact
                                </button>
                              </div>
                            )}
                          </td>
                        ) : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <EmptyState title={subjects ? "No subjects opened in this range." : "No MCP calls in this range."} />
          )}

          {caption ? <div className="muted">{caption}</div> : null}
        </>
      ) : null}
    </div>
  );
}
